import React, { useContext } from "react";
import { Autocomplete, DataTable, FilterBar, Page, SearchBar, hasActiveFilters, useFilterSortPagination, } from "@sscs/ui";
import { categoryToTitle } from "../../../../constants";
import { ReportContext } from "../../../context";
import { useCategoryFilterOptions } from "../../../hooks";
import { useGetIssuesDiffQuery } from "../../../services/diff.api";
import { getQueryStatus } from "../../../utils";
import IssueDetails from "./IssueDetails";
import { CHANGE_LABEL, filterIssueChanges, mergeIssueChanges, sortIssueChanges, } from "./utils";
const CHANGE_OPTIONS = [
    "new",
    "worsened",
    "improved",
    "resolved",
];
/** Initial state for the unified issues diff table filter */
export const initFilter = {
    search: "",
    changes: [],
    category: null,
};
/** Server sort for the full-set fetches (ordering is applied client-side). */
export const initSort = {
    property: "diff_issue_count",
    reverse: true,
};
/** Default client ordering: current file count, descending. */
const defaultSort = {
    property: "now",
    reverse: true,
};
/** Initial state for issues diff table pagination */
export const initPagination = {
    currentPage: 1,
    itemsPerPage: 10,
};
const IssueChanges = ({ stats }) => {
    var _a, _b, _c, _d, _e, _f, _g;
    const { reportId, diffId } = useContext(ReportContext);
    const categoryOptions = useCategoryFilterOptions().filter((category) => category !== "diff-hunting");
    const { filter, sort, pagination, handleFilterChange, handleSortChange, handlePaginationChange, } = useFilterSortPagination(initFilter, defaultSort, initPagination);
    const introducedQuery = useGetIssuesDiffQuery({
        filter: { category: (_a = filter.category) !== null && _a !== void 0 ? _a : "ALL", hide_resolved: false },
        sort: initSort,
        pagination: {
            currentPage: 1,
            itemsPerPage: Math.max(stats.introduced_issues, 1),
        },
        diff_type: "introduced",
        sdlc_report_id: reportId,
        ui_id: diffId,
    });
    const resolvedQuery = useGetIssuesDiffQuery({
        filter: { category: (_b = filter.category) !== null && _b !== void 0 ? _b : "ALL", hide_resolved: false },
        sort: initSort,
        pagination: {
            currentPage: 1,
            itemsPerPage: Math.max(stats.resolved_issues, 1),
        },
        diff_type: "resolved",
        sdlc_report_id: reportId,
        ui_id: diffId,
    });
    const rows = mergeIssueChanges((_d = (_c = introducedQuery.data) === null || _c === void 0 ? void 0 : _c.issues) !== null && _d !== void 0 ? _d : [], (_f = (_e = resolvedQuery.data) === null || _e === void 0 ? void 0 : _e.issues) !== null && _f !== void 0 ? _f : []);
    const filteredRows = filterIssueChanges(rows, filter);
    const sortedRows = sortIssueChanges(filteredRows, sort);
    const pageRows = sortedRows.slice((pagination.currentPage - 1) * pagination.itemsPerPage, pagination.currentPage * pagination.itemsPerPage);
    const columns = [
        { id: "expand", label: "", width: "48px" },
        {
            id: "change",
            label: "Change",
            sortProperty: "change",
            sortId: "diff-sort-change",
            width: "108px",
        },
        {
            id: "id",
            label: "ID",
            sortProperty: "id",
            sortId: "diff-sort-id",
            width: "88px",
        },
        { id: "description", label: "Description", width: "100%" },
        {
            id: "existed",
            label: "Existed",
            sortProperty: "existed",
            sortId: "diff-sort-existed",
            startReverse: true,
            align: "right",
            width: "108px",
        },
        {
            id: "fixed",
            label: "Fixed",
            sortProperty: "fixed",
            sortId: "diff-sort-fixed",
            startReverse: true,
            align: "right",
            width: "108px",
        },
        {
            id: "new",
            label: "New",
            sortProperty: "added",
            sortId: "diff-sort-new",
            startReverse: true,
            align: "right",
            width: "108px",
        },
        {
            id: "now",
            label: "Now",
            sortProperty: "now",
            sortId: "diff-sort-now",
            startReverse: true,
            align: "right",
            width: "108px",
        },
    ];
    return (React.createElement(React.Fragment, null,
        React.createElement(Page.Filters, { found: {
                id: "diff-clear-all-filters",
                count: filteredRows.length,
                initialFilters: initFilter,
                activeFilters: filter,
                onClear: () => handleFilterChange(initFilter),
            } },
            React.createElement(FilterBar, { stacking: "never", filters: [
                    {
                        id: "search",
                        minWidth: "280px",
                        control: (React.createElement(SearchBar, { id: "diff-search", value: filter.search, onChange: (value) => handleFilterChange({ ...filter, search: value }), label: "Search by rule ID or description", slotProps: { htmlInput: { maxLength: 256 } } })),
                    },
                    {
                        id: "change",
                        width: "230px",
                        control: (React.createElement(Autocomplete, { multiple: true, limitTags: 1, id: "diff-filter-change", label: "Change", options: CHANGE_OPTIONS, getOptionLabel: (option) => CHANGE_LABEL[option], value: filter.changes, onChange: (_event, value) => handleFilterChange({ ...filter, changes: value }) })),
                    },
                    {
                        id: "category",
                        width: "280px",
                        control: (React.createElement(Autocomplete, { id: "diff-filter-categories", label: "Filter by category", options: categoryOptions, getOptionLabel: (category) => categoryToTitle[category], value: filter.category, onChange: (_event, value) => handleFilterChange({ ...filter, category: value }) })),
                    },
                ] })),
        React.createElement(Page.Body, null,
            React.createElement(DataTable
            // Force rerender of the table to collapse expanded rows when filter/pagination/sort changes
            , { 
                // Force rerender of the table to collapse expanded rows when filter/pagination/sort changes
                key: `${JSON.stringify(filter)} ${JSON.stringify(sort)} ${JSON.stringify(pagination)}`, columns: columns, sort: sort, onSortChange: handleSortChange, empty: {
                    subject: "issue changes",
                    filters: {
                        active: hasActiveFilters(initFilter, filter),
                        onReset: () => handleFilterChange(initFilter),
                    },
                }, status: getQueryStatus({
                    isLoading: introducedQuery.isLoading || resolvedQuery.isLoading,
                    isFetching: introducedQuery.isFetching || resolvedQuery.isFetching,
                    isError: introducedQuery.isError || resolvedQuery.isError,
                    error: (_g = introducedQuery.error) !== null && _g !== void 0 ? _g : resolvedQuery.error,
                    data: introducedQuery.data && resolvedQuery.data
                        ? sortedRows
                        : undefined,
                }), pagination: {
                    id: "diff-pagination",
                    itemCount: filteredRows.length,
                    pagination,
                    onChange: handlePaginationChange,
                } }, pageRows.map((row, index) => (React.createElement(IssueDetails, { key: row.id, rowIndex: index, issue: row })))))));
};
export default IssueChanges;
//# sourceMappingURL=IssueChanges.js.map